import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useCreateOvertimeRequestMutation } from "../api/queries";
import { OTRequestForm } from "../ui/OTRequestForm";
import type { OTRequestFormValues } from "../ui/OTRequestForm";
import { Button, Card, CardContent, CardHeader, CardTitle, toast } from "@devhop/ui";
import { ArrowLeft, HelpCircle, User } from "lucide-react";
import { OTRequestFormForEmployeeTour, useOTRequestFormForEmployeeTour } from "../tour/OTRequestFormForEmployeeTour";
import { useCallback } from "react";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import type { LookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormRoot, zodResolver } from "@devhop/ui";
import * as RHF from "react-hook-form";
import { z } from "zod";

type SearchParams = { employeeId?: string };

/** Default search for approvals route; required when navigating from this page. */
const APPROVALS_SEARCH = {
  offset: 0,
  limit: 20,
  sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
  periodYear: undefined as number | undefined,
  periodMonth: undefined as number | undefined,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
  employeeId: undefined as string | undefined,
};

const EmployeeFormSchema = z.object({
  employeeId: z.string().min(1, "ກະລຸນາເລືອກພະນັກງານ"),
});

export function OTRequestFormForEmployeePage() {
  const nav = useNavigate({ from: "/app/hr/overtime/requests/new-for-employee" });
  const search = useSearch({ from: "/app/hr/overtime/requests/new-for-employee" }) as SearchParams;
  const createMutation = useCreateOvertimeRequestMutation();
  const tour = useOTRequestFormForEmployeeTour();

  const employeeMethods = RHF.useForm<{ employeeId: string }>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(EmployeeFormSchema) as any,
    defaultValues: { employeeId: search.employeeId ?? "" },
  });
  const selectedEmployeeId = RHF.useWatch({
    control: employeeMethods.control,
    name: "employeeId",
    defaultValue: search.employeeId ?? "",
  }) ?? "";

  const handleBack = useCallback(() => {
    nav({ to: "/app/hr/overtime/approvals", search: APPROVALS_SEARCH });
  }, [nav]);

  const handleSubmit = useCallback(
    (values: OTRequestFormValues) => {
      if (!selectedEmployeeId.trim()) {
        toast.error("ກະລຸນາເລືອກພະນັກງານ");
        return;
      }
      createMutation.mutate(
        {
          workDate: values.workDate,
          startTime: values.startTime,
          endTime: values.endTime,
          reason: values.reason.trim() || undefined,
          employeeId: selectedEmployeeId.trim(),
        },
        {
          onSuccess: () => {
            toast.success("ສົ່ງຄຳຂໍ OT ສຳເລັດ");
            nav({ to: "/app/hr/overtime/approvals", search: APPROVALS_SEARCH });
          },
          onError: (err: { message?: string; status?: number }) => {
            const msg = err?.message ?? "";
            if (msg.includes("Duplicate") || (err as { status?: number })?.status === 409) {
              toast.error("ພະນັກງານມີຄຳຂໍ OT ສຳລັບວັນນີ້ແລ້ວ");
              return;
            }
            toast.error(msg || "ເກີດຂໍ້ຜິດພາດ");
          },
        },
      );
    },
    [selectedEmployeeId, createMutation, nav],
  );

  return (
    <>
      <Header />
      <Main>
        <OTRequestFormForEmployeeTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6 flex items-center justify-between" data-tourid="ot-request-for-employee-back">
          <Button variant="ghost" onClick={handleBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            ກັບຄືນລາຍການອະນຸມັດ
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 rounded-full"
            onClick={tour.startTour}
            title="ເລີ່ມທົດສອບການນຳທາງ"
            aria-label="ເລີ່ມທົດສອບການນຳທາງ"
          >
            <HelpCircle className="size-5" />
          </Button>
        </div>

        <div className="mx-auto w-full max-w-4xl space-y-6">
          <FormRoot<{ employeeId: string }>
            methods={employeeMethods}
            onSubmit={() => {}}
            className="w-full"
          >
            <Card className="w-full" data-tourid="ot-request-for-employee-employee">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  ພະນັກງານທີ່ຕ້ອງການສ້າງຄຳຂໍ OT
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormInfiniteCombobox<LookupItem>
                  name="employeeId"
                  label=""
                  requiredMark
                  queryKey={["users-lookup-ot-for-employee"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກພະນັກງານ..."
                />
              </CardContent>
            </Card>
          </FormRoot>

          <div data-tourid="ot-request-for-employee-form">
          <OTRequestForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={handleBack}
            isSubmitting={createMutation.isPending}
          />
          </div>
        </div>
      </Main>
    </>
  );
}
